import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/layout/AdminLayout';
import { categoryAPI, subCategoryAPI, vendorAPI, budgetPlanAPI } from '../lib/api';
import { 
  Layers, 
  ListTree, 
  Store, 
  Calculator,
  TrendingUp,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const StatCard = ({ icon: Icon, label, value, color, link }) => (
  <Card className="bg-white border-[#E6DCD3] hover:shadow-md transition-shadow duration-300">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[#585858] uppercase tracking-widest mb-2">{label}</p>
          <p className="font-serif text-4xl font-medium text-[#69111F]">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${color}`}>
          <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
        </div>
      </div>
      {link && (
        <Link 
          to={link} 
          className="mt-4 inline-flex items-center text-sm text-[#69111F] hover:underline"
        >
          View Details <ArrowRight className="w-4 h-4 ml-1" />
        </Link>
      )}
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    categories: 0,
    subcategories: 0,
    vendors: 0,
    budgetPlans: 0,
  });
  const [recentPlans, setRecentPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [categories, subcategories, vendors, budgetPlans] = await Promise.all([
        categoryAPI.getAll(),
        subCategoryAPI.getAll(),
        vendorAPI.getAll(),
        budgetPlanAPI.getAll(),
      ]);

      setStats({
        categories: categories.data.length,
        subcategories: subcategories.data.length,
        vendors: vendors.data.length,
        budgetPlans: budgetPlans.data.length,
      });

      // Get 3 most recent budget plans
      const sortedPlans = budgetPlans.data
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 3);
      setRecentPlans(sortedPlans);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="dashboard-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl font-medium text-[#1A1A1A] tracking-tight">
            Dashboard
          </h1>
          <p className="text-[#585858] mt-2">
            Overview of your wedding budget planning system
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard 
            icon={Layers} 
            label="Categories" 
            value={stats.categories}
            color="bg-[#69111F]"
            link="/categories"
          />
          <StatCard 
            icon={ListTree} 
            label="Sub-Categories" 
            value={stats.subcategories}
            color="bg-[#A0525E]"
            link="/subcategories"
          />
          <StatCard 
            icon={Store} 
            label="Vendors" 
            value={stats.vendors}
            color="bg-[#640817]"
            link="/vendors"
          />
          <StatCard 
            icon={Calculator} 
            label="Budget Plans" 
            value={stats.budgetPlans}
            color="bg-[#3D5A46]"
            link="/budget-planner"
          />
        </div>

        {/* Recent Activity & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Budget Plans */}
          <Card className="bg-white border-[#E6DCD3]">
            <CardHeader className="border-b border-[#E6DCD3]">
              <CardTitle className="font-serif text-xl font-medium text-[#1A1A1A] flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#69111F]" />
                Recent Budget Plans
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-[#585858]">Loading...</div>
              ) : recentPlans.length > 0 ? (
                <div className="divide-y divide-[#E6DCD3]">
                  {recentPlans.map((plan) => (
                    <div key={plan.id} className="p-4 hover:bg-[#FDFBF8] transition-colors">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{plan.name}</p>
                          <p className="text-sm text-[#585858]">{plan.client_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-serif text-lg text-[#69111F]">
                            Rp {plan.total_budget.toLocaleString()}
                          </p>
                          <p className="text-xs text-[#8A8A8A]">
                            {new Date(plan.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-[#585858]">
                  No budget plans yet. Create your first one!
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-white border-[#E6DCD3]">
            <CardHeader className="border-b border-[#E6DCD3]">
              <CardTitle className="font-serif text-xl font-medium text-[#1A1A1A]">
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <Link to="/budget-planner" className="block">
                <Button 
                  className="w-full h-14 bg-[#69111F] hover:bg-[#500d18] text-[#F7F0E8] font-medium uppercase tracking-wide rounded-sm justify-start px-6"
                  data-testid="create-budget-plan-btn"
                >
                  <Calculator className="w-5 h-5 mr-3" />
                  Create Budget Plan
                </Button>
              </Link>
              <Link to="/vendors" className="block">
                <Button 
                  variant="outline"
                  className="w-full h-14 border-[#69111F] text-[#69111F] hover:bg-[#69111F] hover:text-[#F7F0E8] font-medium uppercase tracking-wide rounded-sm justify-start px-6"
                  data-testid="add-vendor-btn"
                >
                  <Store className="w-5 h-5 mr-3" />
                  Add New Vendor
                </Button>
              </Link>
              <Link to="/categories" className="block">
                <Button 
                  variant="outline"
                  className="w-full h-14 border-[#E6DCD3] text-[#585858] hover:border-[#69111F] hover:text-[#69111F] font-medium uppercase tracking-wide rounded-sm justify-start px-6"
                  data-testid="manage-categories-btn"
                >
                  <Layers className="w-5 h-5 mr-3" />
                  Manage Categories
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Decorative Image Card */}
        <Card className="mt-6 overflow-hidden border-[#E6DCD3]">
          <div 
            className="h-48 bg-cover bg-center relative"
            style={{ 
              backgroundImage: 'url(https://images.unsplash.com/photo-1739216906046-afc47ed589fb?crop=entropy&cs=srgb&fm=jpg&q=85)'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#69111F]/60 to-transparent" />
            <div className="relative z-10 p-8 flex flex-col justify-center h-full">
              <h3 className="font-serif text-2xl text-white mb-2">
                Plan Beautiful Weddings
              </h3>
              <p className="text-white/80 max-w-md">
                Use KNOTS to manage budgets, coordinate vendors, and create unforgettable celebrations.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
